import { useEffect, useLayoutEffect, useRef } from "react";
import { useChatStore } from "../lib/useChatStore";
import MessageBubble from "./MessageBubble";
import EmptyState from "./EmptyState";

const Thread = () => {
  const { messages, isStreaming } = useChatStore();
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastCountRef = useRef(0);
  const isAtBottomRef = useRef(true);

  // ✅ Estratégia 1: Observar se usuário está "grudado" no fim
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const onScroll = () => {
      const distance = el.scrollHeight - (el.scrollTop + el.clientHeight);
      isAtBottomRef.current = distance < 64; // tolerância de 64px
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    onScroll(); // Estado inicial
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  // ✅ Estratégia 2: Scroll inicial ao montar/entrar na conversa
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: "auto" });
    });
  }, []); // Deps vazias = só no mount

  // ✅ Estratégia 3: Scroll suave quando NOVA mensagem entra (length muda)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    if (messages.length !== lastCountRef.current) {
      lastCountRef.current = messages.length;

      requestAnimationFrame(() => {
        el.scrollTo({
          top: el.scrollHeight,
          behavior: "smooth",
        });
      });
    }
  }, [messages.length]); // ✅ Escuta LENGTH, não array inteiro

  // ✅ Estratégia 4: "Pinned to bottom" durante streaming (sem smooth)
  const lastTextLen =
    messages.length > 0
      ? (messages[messages.length - 1].content?.length ?? 0)
      : 0;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (!isStreaming) return;
    if (!isAtBottomRef.current) return; // Respeita se usuário rolou pra cima

    // Durante tokens: rápido e sem smooth (não trava)
    el.scrollTo({ top: el.scrollHeight, behavior: "auto" });
  }, [isStreaming, lastTextLen]); // Deps: streaming ativo + tamanho do conteúdo

  const handleSuggestionClick = (text: string) => {
    window.dispatchEvent(new CustomEvent("legalchat:suggestion", { detail: text }));
  };

  return (
    <div
      ref={scrollRef}
      className="h-full overflow-y-auto p-4 md:p-6 flex flex-col"
    >
      {messages.length === 0 && !isStreaming ? (
        <EmptyState onSuggestionClick={handleSuggestionClick} isStreaming={isStreaming} />
      ) : (
        <div className="w-[80%] mx-auto space-y-6">
          {messages.map((msg, idx) => (
            <MessageBubble
              key={msg.id || idx}
              role={msg.role}
              content={msg.content}
              id={msg.id}
              created_at={msg.created_at}
              isStreaming={isStreaming && idx === messages.length - 1}
              sources={msg.metadata?.sources}
            />
          ))}

          {isStreaming && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <div className="flex gap-1">
                <span className="animate-bounce">•</span>
                <span className="animate-bounce delay-75">•</span>
                <span className="animate-bounce delay-150">•</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Thread;
